import axios from 'axios';

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

// Public API — no auth token required. withCredentials so the httpOnly
// refresh cookie set on activation is stored by the browser.
const publicApi = axios.create({
  baseURL: `${API_URL}/api/v1/public/onboarding`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'jurivoxa-web' },
});

export type WorkspaceType = 'solo' | 'chambers';
export type OnboardingPlan = 'solo' | 'starter' | 'professional' | 'enterprise';
export type OnboardingStatus =
  | 'pending_details'
  | 'pending_payment'
  | 'payment_confirmed'
  | 'provisioning'
  | 'provisioned'
  | 'activated'
  | 'expired'
  | 'failed';

export type ProvisioningStep = {
  step: string;
  status: 'ok' | 'failed';
  message?: string;
  completedAt: string;
};

export interface SessionStatus {
  id: string;
  token: string;
  workspaceType: WorkspaceType;
  plan: string;
  email: string;
  firstName: string;
  lastName: string;
  organisationName?: string;
  status: OnboardingStatus;
  paidAt?: string;
  provisionedAt?: string;
  activatedAt?: string;
  expiresAt: string;
  provisioning?: {
    status: string;
    steps: ProvisioningStep[];
    errorMessage?: string;
    completedAt?: string;
  };
  activationUrl?: string;
}

export async function startSession(data: {
  workspaceType: WorkspaceType;
  plan: OnboardingPlan;
  email: string;
  firstName: string;
  lastName: string;
  organisationName?: string;
}): Promise<{ token: string }> {
  const res = await publicApi.post('/start', data);
  return res.data.data;
}

export async function getSessionStatus(token: string): Promise<SessionStatus> {
  const res = await publicApi.get(`/${token}/status`);
  return res.data.data;
}

/**
 * Release the session to provisioning. NO CARD IS COLLECTED AND NO PAYMENT IS TAKEN.
 *
 * This used to take `{ cardName, last4 }`, which required the signup page to hold a full card
 * number and CVV in its own DOM in order to derive them. Nothing charged anything, the server
 * discarded the values, and billing is Future work (CAPABILITY-REGISTER.md). The body is now
 * empty and the server's schema is `.strict()`, so adding a card field back here does not
 * silently start working — it gets a 400.
 */
export async function confirmPayment(token: string): Promise<{ paymentRef: string }> {
  const res = await publicApi.post(`/${token}/payment`, {});
  return res.data.data;
}

export interface ActivationSession {
  accessToken: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    memberType: string;
    avatarUrl: string | null;
    chambersId: string;
    barristerId: string | null;
  };
  chambers: {
    id: string;
    name: string;
    slug: string;
    logoUrl: string | null;
    currency: string;
    jurisdiction: string;
    timezone: string;
  };
  permissions: string[];
}

export async function activateAccount(data: {
  inviteToken: string;
  password: string;
}): Promise<ActivationSession> {
  const res = await publicApi.post('/activate', data);
  return res.data.data;
}
